import { useState, useContext, useEffect } from 'react';
import { UserContext } from '../../../contexts/userProvider';
import { PatrimonioContext } from '../../../contexts/patrimonioProvider';
import { FornecedoresContext } from '../../../contexts/fornecedoresProvider';
import { ClientContext } from '../../../contexts/clientProvider';
import { AnexosContext } from '../../../contexts/anexosProvider';
import DataTable from '../../Table';
import LoadingSpinner from '../../Loader/load';
import { cnpj } from 'cpf-cnpj-validator';
import ProdutoEdit from '../Type/produtoEdit';
import EquipamentoEdit from '../Type/equipamentoEdit';
import TipoImportacao from '../Type/TipoImportacao';
import SelecaoGlobalEquipamentos from '../Type/SelecaoGlobalEquipamentos';
import PinConfirmation from '../Type/PinConfirmation';
import { downloadEquipamentosTemplate, processEquipamentosXlsxFile } from '../Utils/xlsxEquipamentosProcessor';

const Importar = () => {
    const { user, setAlert } = useContext(UserContext);
    const { criarPatrimonioNfsByXml, cadastroMassaPatrimonios } = useContext(PatrimonioContext);
    const { allMarcas, allModelos, fornecedores } = useContext(FornecedoresContext);
    const { clientes, getClientes } = useContext(ClientContext);
    const anexosContext = useContext(AnexosContext);
    const [xmlData, setXmlData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [produtoEdit, setProdutoEdit] = useState(null)
    const [marcas, setMarcas] = useState([]);
    const [modelos, setModelos] = useState([]);
    const [tipoImportacao, setTipoImportacao] = useState(null);
    const [equipamentosData, setEquipamentosData] = useState(null);
    const [equipamentoEdit, setEquipamentoEdit] = useState(null);
    const [showPinModal, setShowPinModal] = useState(false);

    useEffect(() => {
        receiveMarcasModelos();
        receiveClientes();
    }, [])

    const receiveMarcasModelos = async () => {
        let marcas = await allMarcas();
        let modelos = await allModelos();
        setMarcas(marcas);
        setModelos(modelos);
    }

    const receiveClientes = async () => {
        if (getClientes && !clientes) {
            await getClientes();
        }
    }


    const handleFileChange = (event) => {
        const file = event?.target?.files[0];
        if (!file) return;

        const fileType = file?.name.split('.').pop().toLowerCase();
        if (fileType !== 'xml') {
            setAlert({
                visible: true,
                title: "Atenção!",
                placeholder: 'Selecione um arquivo XML válido',
                confirm: false,
                type: "warning"
            });
            return;
        }

        const reader = new FileReader();

        reader.onload = (event) => {
            setLoading(true);
            const xmlString = event.target.result;
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

            if (xmlDoc.documentElement.nodeName === 'parsererror') {
                setAlert({
                    visible: true,
                    title: "Erro!",
                    placeholder: 'Erro ao carregar o XML. Verifique se o arquivo é válido.',
                    confirm: false,
                    type: "warning"
                });
                setLoading(false);
                return;
            }

            try {
                const nomeFornecedor = xmlDoc.querySelector('xNome')?.textContent || 'Não informado';
                const cnpjValue = xmlDoc.querySelector('CNPJ')?.textContent || '';
                const endereco = xmlDoc.querySelector('xLgr')?.textContent || 'Não informado';
                const numero = xmlDoc.querySelector('nro')?.textContent || 'S/N';
                const cidade = xmlDoc.querySelector('xMun')?.textContent || 'Não informado';
                const uf = xmlDoc.querySelector('UF')?.textContent || 'Não informado';
                const telefone = xmlDoc.querySelector('fone')?.textContent || 'Não informado';
                const numeroNota = xmlDoc.querySelector('nNF')?.textContent || 'Não informado';
                let dataEmissao = xmlDoc.querySelector('dhEmi')?.textContent || new Date().toISOString();
                const valorTotalNFE = xmlDoc.querySelector('vNF')?.textContent || '0';

                const produtos = [];
                const produtosXML = xmlDoc.querySelectorAll('det');

                if (produtosXML.length === 0) {
                    setAlert({
                        visible: true,
                        title: "Atenção!",
                        placeholder: 'Nenhum produto encontrado no XML',
                        confirm: false,
                        type: "warning"
                    });
                    setLoading(false);
                    return;
                }

                produtosXML.forEach((produtoXML, index) => {
                    const produtoNome = produtoXML.querySelector('xProd')?.textContent || `Produto ${index + 1}`;
                    const quantidade = produtoXML.querySelector('qCom')?.textContent || '0';
                    const valorUnitario = produtoXML.querySelector('vUnCom')?.textContent || '0';
                    const valorTotal = produtoXML.querySelector('vProd')?.textContent || '0';

                    const valorUnitarioFormatado = `R$ ${parseFloat(valorUnitario).toFixed(2)}`;
                    const valorTotalFormatado = `R$ ${parseFloat(valorTotal).toFixed(2)}`;
                    const quantidadeFormatada = parseFloat(quantidade).toFixed(0);

                    let marcaEncontrada = null;
                    let modeloEncontrado = null;

                    marcaEncontrada = marcas.find((marca) =>
                        produtoNome.toLowerCase().includes(marca.nome.toLowerCase())
                    );

                    modeloEncontrado = modelos.find((modelo) =>
                        produtoNome.toLowerCase().includes(modelo.nome.toLowerCase())
                    );

                    if (!marcaEncontrada && modeloEncontrado) {
                        marcaEncontrada = marcas.find((marca) => marca.id === modeloEncontrado.id_marca);
                    }

                    produtos.push({
                        id: index + 1,
                        nome: produtoNome,
                        quantidade: quantidadeFormatada,
                        valorUnitario: valorUnitarioFormatado,
                        valorTotal: valorTotalFormatado,
                        fornecedor: null,
                        marca: marcaEncontrada?.id || null,
                        modelo: modeloEncontrado?.id || null
                    });
                });

                const data = new Date(dataEmissao);
                const dia = data.getDate().toString().padStart(2, '0');
                const mes = (data.getMonth() + 1).toString().padStart(2, '0');
                const ano = data.getFullYear();
                const dataEmissaoFormatada = `${dia}/${mes}/${ano}`;

                setXmlData({
                    nomeFornecedor,
                    cnpj: cnpjValue,
                    endereco,
                    numero,
                    cidade,
                    uf,
                    telefone,
                    produtos,
                    numeroNota,
                    dataEmissao: dataEmissaoFormatada,
                    valorTotalNFE,
                    user
                });
                setLoading(false);

            } catch (error) {
                console.error('Erro ao processar XML:', error);
                setAlert({
                    visible: true,
                    title: "Erro!",
                    placeholder: 'Erro ao processar o XML. Verifique se o arquivo está correto.',
                    confirm: false,
                    type: "warning"
                });
                setLoading(false);
            }
        };

        reader.onerror = () => {
            setAlert({
                visible: true,
                title: "Erro!",
                placeholder: 'Erro ao ler o arquivo XML',
                confirm: false,
                type: "warning"
            });
            setLoading(false);
        };

        reader.readAsText(file);
    };

    const handleEquipamentosFileChange = async (event) => {
        const file = event?.target?.files[0];
        if (!file) return;

        const fileType = file?.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls'].includes(fileType)) {
            setAlert({
                visible: true,
                title: "Atenção!",
                placeholder: 'Selecione um arquivo Excel válido (.xlsx ou .xls)',
                confirm: false,
                type: "warning"
            });
            return;
        }

        setLoading(true);

        try {
            const result = await processEquipamentosXlsxFile(file, marcas, modelos);
            setEquipamentosData({ ...result, user });
            setLoading(false);

            setAlert({
                visible: true,
                title: "Sucesso!",
                placeholder: `Planilha de equipamentos carregada com sucesso! ${result.equipamentos.length} equipamento(s) encontrado(s).`,
                confirm: false,
                type: "success"
            });

        } catch (error) {
            console.error('Erro ao processar planilha de equipamentos:', error);
            setAlert({
                visible: true,
                title: "Erro!",
                placeholder: error.message || 'Erro ao processar a planilha de equipamentos. Verifique se o arquivo está no formato correto.',
                confirm: false,
                type: "warning"
            });
            setLoading(false);
        }
    };

    const confirmarEquipamentos = () => {
        if (!equipamentosData) {
            setAlert({ visible: true, title: "Atenção!", placeholder: "Nenhum equipamento para confirmar", confirm: false, type: "warning" });
            return;
        }
        setShowPinModal(true);
    };

    const confirmarEquipamentosComPin = async () => {
        try {
            setLoading(true);
            const resultado = await cadastroMassaPatrimonios(equipamentosData);

            if (resultado?.success) {
                setEquipamentosData(null);
                setTipoImportacao(null);
            }
        } catch (error) {
            console.error('Erro ao confirmar equipamentos:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePinConfirm = () => {
        setShowPinModal(false);
        confirmarEquipamentosComPin();
    };

    const handlePinCancel = () => {
        setShowPinModal(false);
    };

    const handleDownloadTemplate = () => {
        try {
            downloadEquipamentosTemplate();
            setAlert({
                visible: true,
                title: "Sucesso!",
                placeholder: 'Template de planilha de equipamentos baixado com sucesso!',
                confirm: false,
                type: "success"
            });
        } catch (error) {
            console.error('Erro ao baixar template:', error);
            setAlert({
                visible: true,
                title: "Erro!",
                placeholder: 'Erro ao baixar o template da planilha.',
                confirm: false,
                type: "warning"
            });
        }
    };

    const handleTipoSelecionado = (tipo) => {
        setTipoImportacao(tipo);
        if (xmlData) {
            setXmlData(null);
        }
        if (equipamentosData) {
            setEquipamentosData(null);
        }
    };

    const visualizar = (id) => {
        let produto = xmlData.produtos.find((produto) => produto.id === id);
        setProdutoEdit(produto);
    }

    const visualizarEquipamento = (id) => {
        let equipamento = equipamentosData.equipamentos.find((equipamento) => equipamento.id === id);
        setEquipamentoEdit(equipamento);
    }

    const salvarProduto = (produto) => {
        let produtos = xmlData.produtos.map((item) => {
            if (item.id === produto.id) {
                return {
                    ...produto,
                    fornecedor: produto.fornecedor ? parseInt(produto.fornecedor) : null,
                    marca: produto.marca ? parseInt(produto.marca) : null,
                    modelo: produto.modelo ? parseInt(produto.modelo) : null
                };
            }
            return item;
        });

        setXmlData({ ...xmlData, produtos });

        const isCompleto = produto.fornecedor && produto.marca && produto.modelo;
        const isParcial = produto.fornecedor || produto.marca || produto.modelo;

        setProdutoEdit(null);

        if (isCompleto) {
            setAlert({ visible: true, title: "Sucesso!", placeholder: "Produto classificado completamente!", confirm: false, type: "success" });
        } else if (isParcial) {
            setAlert({ visible: true, title: "Produto Salvo!", placeholder: "Classificação parcial salva. Você pode completar depois se necessário.", confirm: false, type: "success" });
        } else {
            setAlert({ visible: true, title: "Produto Salvo!", placeholder: "Produto salvo sem classificação. Você pode classificar depois se necessário.", confirm: false, type: "success" });
        }
    }

    const salvarEquipamento = (equipamento) => {
        let equipamentos = equipamentosData.equipamentos.map((item) => {
            if (item.id === equipamento.id) {
                return {
                    ...equipamento,
                    cliente: equipamento.cliente ? parseInt(equipamento.cliente) : null,
                    anexo: equipamento.anexo ? parseInt(equipamento.anexo) : null,
                    fornecedor: equipamento.fornecedor ? parseInt(equipamento.fornecedor) : null,
                    marca: equipamento.marca ? parseInt(equipamento.marca) : null,
                    modelo: equipamento.modelo ? parseInt(equipamento.modelo) : null
                };
            }
            return item;
        });

        setEquipamentosData({ ...equipamentosData, equipamentos });

        const isCompleto = equipamento.cliente && equipamento.marca && equipamento.modelo;
        const isParcial = equipamento.cliente || equipamento.anexo || equipamento.fornecedor || equipamento.marca || equipamento.modelo;

        setEquipamentoEdit(null);

        if (isCompleto) {
            setAlert({ visible: true, title: "Sucesso!", placeholder: "Equipamento classificado completamente!", confirm: false, type: "success" });
        } else if (isParcial) {
            setAlert({ visible: true, title: "Equipamento Salvo!", placeholder: "Classificação parcial salva. Você pode completar depois se necessário.", confirm: false, type: "success" });
        } else {
            setAlert({ visible: true, title: "Equipamento Salvo!", placeholder: "Equipamento salvo sem classificação. Você pode classificar depois se necessário.", confirm: false, type: "success" });
        }
    }

    const headers = [
        { label: "Produto", key: "nome", sortable: true },
        { label: "Quantidade", key: "quantidade", sortable: false },
        { label: "Valor Unitário", key: "valorUnitario", sortable: false },
        { label: "Valor Total", key: "valorTotal", sortable: false },
        { label: "Status", key: "status", sortable: false },
        { label: "Ações", key: "actions", sortable: false, actions: { "editar": visualizar } }
    ]

    const produtosComStatus = xmlData?.produtos?.map(produto => {
        const temFornecedor = !!produto.fornecedor;
        const temMarca = !!produto.marca;
        const temModelo = !!produto.modelo;
        const totalCampos = (temFornecedor ? 1 : 0) + (temMarca ? 1 : 0) + (temModelo ? 1 : 0);

        let status;
        if (totalCampos === 3) {
            status = "✅ Completo";
        } else if (totalCampos === 2) {
            status = "🔶 Quase completo";
        } else if (totalCampos === 1) {
            status = "⚠️ Parcial";
        } else {
            status = "⭕ Pendente";
        }

        return { ...produto, status };
    }) || [];

    const equipamentosHeaders = [
        { label: "Número de Série", key: "nserie", sortable: true },
        { label: "MAC", key: "nmac", sortable: false },
        { label: "Etiqueta", key: "netiqueta", sortable: true },
        { label: "Status", key: "status", sortable: false },
        { label: "Ações", key: "actions", sortable: false, actions: { "editar": visualizarEquipamento } }
    ]

    const equipamentosComStatus = equipamentosData?.equipamentos?.map(equipamento => {
        const temCliente = !!equipamento.cliente;
        const temAnexo = !!equipamento.anexo;
        const temFornecedor = !!equipamento.fornecedor;
        const temMarca = !!equipamento.marca;
        const temModelo = !!equipamento.modelo;
        const totalCampos = (temCliente ? 1 : 0) + (temAnexo ? 1 : 0) + (temFornecedor ? 1 : 0) + (temMarca ? 1 : 0) + (temModelo ? 1 : 0);

        let status;
        if (totalCampos >= 4) {
            status = "✅ Completo";
        } else if (totalCampos >= 3) {
            status = "🔶 Quase completo";
        } else if (totalCampos >= 1) {
            status = "⚠️ Parcial";
        } else {
            status = "⭕ Pendente";
        }

        return { ...equipamento, status };
    }) || [];

    const handleCriarPatrimonio = () => {
        let newData = { ...xmlData }

        if (!xmlData || xmlData.produtos.length === 0) {
            setAlert({ visible: true, title: "Atenção!", placeholder: 'Nenhum produto encontrado na nota fiscal', confirm: false, type: "warning" });
            return;
        }

        const produtosCompletos = [];
        const produtosIncompletos = [];
        const produtosSemClassificacao = [];

        for (let i = 0; i < newData.produtos.length; i++) {
            const produto = newData.produtos[i];
            const temTodos = produto.fornecedor && produto.marca && produto.modelo;
            const temAlgum = produto.fornecedor || produto.marca || produto.modelo;

            if (temTodos) {
                produtosCompletos.push(produto.nome);
            } else if (temAlgum) {
                produtosIncompletos.push(produto.nome);
            } else {
                produtosSemClassificacao.push(produto.nome);
            }
        }

        if (produtosIncompletos.length > 0 || produtosSemClassificacao.length > 0) {
            const mensagemAviso = [];
            if (produtosIncompletos.length > 0) {
                mensagemAviso.push(`${produtosIncompletos.length} produto(s) com classificação incompleta`);
            }
            if (produtosSemClassificacao.length > 0) {
                mensagemAviso.push(`${produtosSemClassificacao.length} produto(s) sem classificação`);
            }

            const avisoCompleto = `Atenção: ${mensagemAviso.join(' e ')}. Deseja continuar mesmo assim?`;

            setAlert({
                type: 'confirm',
                placeholder: avisoCompleto,
                visible: true,
                confirm: true,
                title: 'Produtos com Classificação Pendente',
                callback: () => {
                    criarPatrimonioNfsByXml(newData);
                    setXmlData(null);
                }
            });
            return;
        }

        const totalProdutos = newData.produtos.length;
        const valorTotal = parseFloat(xmlData.valorTotalNFE).toFixed(2);

        setAlert({
            type: 'confirm',
            placeholder: `Confirmar entrada de ${totalProdutos} produto(s) no estoque no valor total de R$ ${valorTotal}?`,
            visible: true,
            confirm: true,
            title: 'Confirmar Entrada de Estoque',
            callback: () => {
                criarPatrimonioNfsByXml(newData);
                setXmlData(null);
            }
        });
    }

    const closeEditModal = () => {
        setProdutoEdit(null);
    }

    const closeEquipamentoEditModal = () => {
        setEquipamentoEdit(null);
    }

    const handleAplicarSelecaoGlobal = (selecaoGlobal) => {
        if (!equipamentosData || !equipamentosData.equipamentos) {
            setAlert({ visible: true, title: "Erro!", placeholder: "Nenhum equipamento encontrado para aplicar seleção global.", confirm: false, type: "warning" });
            return;
        }

        const equipamentosAtualizados = equipamentosData.equipamentos.map(equipamento => {
            const equipamentoAtualizado = { ...equipamento };

            if (selecaoGlobal.cliente !== '') {
                equipamentoAtualizado.cliente = selecaoGlobal.cliente;
            }
            if (selecaoGlobal.anexo !== '') {
                equipamentoAtualizado.anexo = selecaoGlobal.anexo;
            }
            if (selecaoGlobal.marca !== '') {
                equipamentoAtualizado.marca = selecaoGlobal.marca;
            }
            if (selecaoGlobal.modelo !== '') {
                equipamentoAtualizado.modelo = selecaoGlobal.modelo;
            }

            return equipamentoAtualizado;
        });

        setEquipamentosData({ ...equipamentosData, equipamentos: equipamentosAtualizados });

        const camposAplicados = [];
        if (selecaoGlobal.cliente !== '') camposAplicados.push('Cliente');
        if (selecaoGlobal.anexo !== '') camposAplicados.push('Anexo');
        if (selecaoGlobal.marca !== '') camposAplicados.push('Marca');
        if (selecaoGlobal.modelo !== '') camposAplicados.push('Modelo');

        setAlert({
            visible: true,
            title: "Sucesso!",
            placeholder: `Seleção global aplicada com sucesso! ${camposAplicados.join(', ')} aplicado(s) a ${equipamentosAtualizados.length} equipamento(s).`,
            confirm: false,
            type: "success"
        });
    }

    return (
        <div className='entrada-container'>
            <div className='entrada-header'>
                <h1>Entrada de Produtos no Estoque</h1>
            </div>
            <div className='entrada-body'>
                {!xmlData && (
                    <TipoImportacao
                        onTipoSelecionado={handleTipoSelecionado}
                        onDownloadTemplate={handleDownloadTemplate}
                    />
                )}

                {tipoImportacao && !xmlData && !equipamentosData && (
                    <div className='input-box' data-tour-id="entrada:arquivo">
                        {tipoImportacao === 'xml' ? (
                            <input
                                type="file"
                                accept=".xml"
                                onChange={handleFileChange}
                                className='entrada-button'
                            />
                        ) : (
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleEquipamentosFileChange}
                                className='entrada-button'
                            />
                        )}
                    </div>
                )}

                {(xmlData || equipamentosData) && (
                    <div className='input-box'>
                        <button
                            className='entrada-limpar-button'
                            onClick={() => {
                                setXmlData(null);
                                setEquipamentosData(null);
                                setTipoImportacao(null);
                            }}
                        >
                            Remover {xmlData ? 'Nota' : 'Planilha de Equipamentos'}
                        </button>
                    </div>
                )}
                {xmlData && (
                    <>
                        <div className='nota-info-box'>
                            <div className='nota-info-header'>
                                <h3 className="text-lg font-semibold mb-3">
                                    Informações da Nota Fiscal
                                </h3>
                                <p>Nome do fornecedor: {xmlData.nomeFornecedor}</p>
                                <p>CNPJ: {xmlData.cnpj ? cnpj.format(xmlData.cnpj) : 'Não informado'}</p>
                                <p>Endereço: {xmlData.endereco}, {xmlData.numero}, {xmlData.cidade}, {xmlData.uf}</p>
                                <p>Telefone: {xmlData.telefone}</p>
                                <p>Número da Nota: {xmlData.numeroNota}</p>
                                <p>Data de Emissão: {xmlData.dataEmissao}</p>
                                <p>Valor Total: R$ {parseFloat(xmlData.valorTotalNFE).toFixed(2)}</p>
                            </div>
                            <h2>Produtos:</h2>
                            {loading ? <LoadingSpinner /> : <DataTable headers={headers} data={produtosComStatus} itemsPerPage={5} />}
                        </div>
                        <button className='entrada-limpar-button' style={{ marginBottom: "20px" }} onClick={handleCriarPatrimonio}>Confirmar entrada de estoque</button>
                    </>
                )}

                {equipamentosData && (
                    <>
                        <SelecaoGlobalEquipamentos
                            equipamentos={equipamentosData.equipamentos}
                            onAplicarSelecaoGlobal={handleAplicarSelecaoGlobal}
                            marcas={marcas}
                            modelos={modelos}
                        />

                        <div className='nota-info-box'>
                            <div className='nota-info-header'>
                                <h3 className="text-lg font-semibold mb-3">
                                    Informações dos Equipamentos
                                </h3>
                                <p>Total de Equipamentos: {equipamentosData.totalEquipamentos}</p>
                                <p>Tipo de Importação: Planilha de Equipamentos</p>
                            </div>
                            <h2>Equipamentos:</h2>
                            {loading ? <LoadingSpinner /> : <DataTable headers={equipamentosHeaders} data={equipamentosComStatus} itemsPerPage={5} />}
                        </div>
                        <button className='entrada-limpar-button' style={{ marginBottom: "20px" }} onClick={confirmarEquipamentos}>Confirmar equipamentos</button>
                    </>
                )}

                {produtoEdit && (
                    <ProdutoEdit produto={produtoEdit} salvarProduto={salvarProduto} closeEditModal={closeEditModal} marcas={marcas} modelos={modelos} />
                )}

                {equipamentoEdit && (
                    <EquipamentoEdit
                        equipamento={equipamentoEdit}
                        salvarEquipamento={salvarEquipamento}
                        closeEditModal={closeEquipamentoEditModal}
                        marcas={marcas}
                        modelos={modelos}
                    />
                )}

                <PinConfirmation
                    isOpen={showPinModal}
                    onConfirm={handlePinConfirm}
                    onCancel={handlePinCancel}
                    title="Confirmar Entrada de Equipamentos"
                />
            </div>
        </div>
    );
};

export default Importar;
